import React, { useState, useMemo } from 'react';

/* Ordenar una tabla por una de sus fechas tocando el encabezado.

   El primer toque pone lo más reciente arriba; el segundo, lo más viejo. Las
   filas sin esa fecha van siempre al final. Antes de tocar nada, la tabla queda
   en el orden en que vino del servidor.

   Va como hook y se llama al principio del componente, antes de cualquier
   "Cargando…": un hook después de un return condicional rompe la pantalla entera. */
export function useOrdenFecha(filas) {
  const [orden, setOrden] = useState(null);   // { campo, dir: 'desc' | 'asc' }
  const ordenadas = useMemo(() => {
    const lista = Array.isArray(filas) ? filas : [];
    if (!orden) return lista;
    const clave = (x) => String(x?.[orden.campo] || '').slice(0, 10);
    return [...lista].sort((a, b) => {
      const ka = clave(a); const kb = clave(b);
      if (!ka && !kb) return 0;
      if (!ka) return 1;
      if (!kb) return -1;
      return orden.dir === 'desc' ? kb.localeCompare(ka) : ka.localeCompare(kb);
    });
  }, [filas, orden]);
  const alternar = (campo) => setOrden(o => (o && o.campo === campo
    ? { campo, dir: o.dir === 'desc' ? 'asc' : 'desc' }
    : { campo, dir: 'desc' }));
  // El encabezado que se puede tocar, con una flechita que dice cómo está ordenado
  const thOrden = (campo, texto, style = {}) => (
    <th style={{ ...style, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => alternar(campo)}
      title={orden?.campo === campo && orden.dir === 'desc' ? 'Ver de los más viejos a los más recientes' : 'Ver de los más recientes a los más viejos'}>
      {texto} <span style={{ opacity: orden?.campo === campo ? 1 : 0.35 }}>{orden?.campo === campo ? (orden.dir === 'desc' ? '↓' : '↑') : '↕'}</span>
    </th>
  );
  return { ordenadas, thOrden };
}

export const money = (v, dec = 0) => '$' + Number(v || 0).toLocaleString('es-AR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
export const moneyK = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e6) return '$' + (n / 1e6).toLocaleString('es-AR', { maximumFractionDigits: 1 }) + ' M';
  if (Math.abs(n) >= 1e3) return '$' + Math.round(n / 1e3).toLocaleString('es-AR') + ' mil';
  return money(n);
};
export const fecha = (d) => {
  if (!d) return '—';
  const s = String(d).slice(0, 10).split('-');
  return `${s[2]}/${s[1]}/${s[0]}`;
};
/* Fecha con hora, en horario argentino. Para saber si alguien entró al portal la
   hora importa tanto como el día: no es lo mismo un martes a las 10 que un
   domingo a la madrugada. */
export const fechaHora = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return fecha(d);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
};

/* Fecha compacta para las tablas densas: 21/09/26.
   En una tabla de cobros el siglo no aporta nada y roba ancho de columna. */
export const fechaCorta = (d) => {
  if (!d) return '—';
  const s = String(d).slice(0, 10).split('-');
  return s.length === 3 ? `${s[2]}/${s[1]}/${s[0].slice(2)}` : '—';
};

export const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
export const mesLabel = (m) => { const [y, mm] = String(m).split('-'); return `${MESES[Number(mm) - 1]} ${String(y).slice(2)}`; };
export const hoy = () => new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10);

/* Hace cuánto que no se toca el precio de un servicio.

   El umbral depende del tipo: un abono mensual que hace 6 meses no se ajusta
   está atrasado, pero un hosting se ajusta una vez al año — marcarlo en rojo a
   los 6 meses sería ruido. Vive acá porque lo miran la ficha del cliente, la
   pantalla de Abonos y la de Hosting. */
export const ajustePrecio = (fechaAjuste, tipo = 'abono') => {
  const anual = tipo === 'anual';
  if (!fechaAjuste) return { texto: 'nunca se ajustó', color: '#ef4444', alerta: true, meses: null };
  const meses = Math.round((Date.now() - new Date(fechaAjuste)) / 2592000000);
  const texto = meses < 1 ? 'ajustado este mes' : `ajustado hace ${meses} mes${meses === 1 ? '' : 'es'}`;
  const rojo = anual ? 11 : 6;
  const amarillo = anual ? 8 : 3;
  return {
    texto, meses,
    color: meses >= rojo ? '#ef4444' : meses >= amarillo ? '#f59e0b' : '#22c55e',
    alerta: meses >= amarillo,
  };
};

export const ESTADO_COBRO = {
  pendiente: { label: 'Pendiente', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  vencido: { label: 'Vencido', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
  informado: { label: 'Informado', color: '#0ea5e9', bg: 'rgba(14,165,233,0.15)' },
  pagado: { label: 'Pagado', color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  anulado: { label: 'Anulado', color: '#64748b', bg: 'rgba(100,116,139,0.15)' },
};
export const ESTADO_RENOVACION = {
  por_preparar: { label: 'Por preparar', color: '#f59e0b' },
  preparada: { label: 'Preparada', color: '#0ea5e9' },
  enviada: { label: 'Enviada', color: '#a855f7' },
  renovada: { label: 'Renovó', color: '#22c55e' },
  no_renovo: { label: 'No renovó', color: '#ef4444' },
  en_gracia: { label: 'Período de gracia', color: '#f97316' },
};

export const Badge = ({ label, color, bg }) => (
  <span className="badge" style={{ background: bg || 'transparent', color: color || 'var(--text-secondary)', border: bg ? 'none' : '1px solid var(--border)', fontSize: 11 }}>{label}</span>
);

// Un dato importante: acento de color, ícono opcional y el contexto abajo
export const Kpi = ({ label, value, sub, color, onClick, icono: Icono }) => {
  const c = color && color !== 'var(--text-primary)' ? color : 'var(--accent)';
  const suave = c.startsWith('#') ? c + '22' : 'var(--accent-glow)';
  return (
    <div className={'card card-dato' + (onClick ? ' clickeable' : '')}
      style={{ '--dato-color': c, '--dato-suave': suave }} onClick={onClick}>
      {Icono && <div className="dato-icono"><Icono size={18} /></div>}
      <div style={{ minWidth: 0 }}>
        <div className="dato-label">{label}</div>
        <div className="dato-valor" style={{ color: color || 'var(--text-primary)' }}>{value}</div>
        {sub && <div className="dato-sub">{sub}</div>}
      </div>
    </div>
  );
};

export const Title = ({ children, right, seccion = 'Agencia' }) => (
  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 22, flexWrap: 'wrap' }}>
    <div>
      <div className="encabezado-eyebrow">{seccion}</div>
      <h1 className="encabezado-titulo">{children}</h1>
      <div className="encabezado-linea" />
    </div>
    {right}
  </div>
);

export const Empty = ({ children }) => <p style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0' }}>{children}</p>;
